import {
  ForwardedRef,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { getIndexByElement, getOffsetParams, isEndOfScrollReached } from './utils';

const useInterval = (callback: () => void, delay: number | undefined, deps = []) => {
  useEffect(() => {
    if (!delay) return;

    const id = setInterval(() => {
      callback();
    }, delay);

    return () => clearInterval(id);
  }, [delay, callback, ...deps]);
};

const useScrollEnd = (
  ref: RefObject<HTMLDivElement | null>,
  callback: (event: Event) => void,
  delay = 300,
) => {
  const timeoutIdRef = useRef<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleScrollEnd = (event: Event) => {
      callback(event);
    };

    const handleScroll = (event: Event) => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      timeoutIdRef.current = window.setTimeout(() => {
        handleScrollEnd(event);
      }, delay);
    };

    const isScrollEndSupported = 'onscrollend' in window;

    if (isScrollEndSupported) {
      element.addEventListener('scrollend', handleScrollEnd);
    } else {
      element.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (isScrollEndSupported) {
        element.removeEventListener('scrollend', handleScrollEnd);
      } else {
        element.removeEventListener('scroll', handleScroll);
      }

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [ref, callback, delay]);
};

type UseSlidesType = {
  step?: number;
  itemsCount: number;
  onScrollLeft?: () => void;
  onScrollRight?: () => void;
  ref: ForwardedRef<HTMLDivElement>;
  autoScrollInterval?: number;
  infinite?: boolean;
  pauseOnHover?: boolean;
};

export const useSlides = ({
  step = 1,
  itemsCount,
  onScrollLeft,
  onScrollRight,
  ref,
  autoScrollInterval: autoScrollIntervalMs,
  infinite,
  pauseOnHover,
}: UseSlidesType) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [canSlideForward, setCanSlideForward] = useState(infinite || false);
  const [canSlideBack, setCanSlideBack] = useState(infinite || false);
  const [autoScrollInterval, setAutoScrollInterval] = useState(autoScrollIntervalMs);

  const carouselRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => carouselRef.current!);

  const performBrowserScroll = (offset: number, behavior: ScrollBehavior): void => {
    setIsScrolling(true);
    carouselRef.current?.scrollTo({ left: offset, behavior });
  };

  const checkCanSlide = () => {
    if (!carouselRef.current) return;

    setCanSlideBack(carouselRef.current.scrollLeft > 0);
    setCanSlideForward(!isEndOfScrollReached(carouselRef.current));
  };

  const recenter = useCallback(() => {
    let nextSlideIndex = slideIndex;

    if (nextSlideIndex === 0) {
      nextSlideIndex = itemsCount;
    } else if (nextSlideIndex > itemsCount) {
      nextSlideIndex = nextSlideIndex % itemsCount;
    }

    if (nextSlideIndex === slideIndex) return;

    const { offset } = getOffsetParams(carouselRef, nextSlideIndex);

    setSlideIndex(nextSlideIndex);
    performBrowserScroll(offset, 'auto');
  }, [itemsCount, slideIndex]);

  useLayoutEffect(() => {
    if (infinite) {
      recenter();
    }

    checkCanSlide();
  }, [slideIndex, infinite, recenter]);

  const slide = useCallback(
    (newSlideIndex: number): void => {
      const isSlideBack = newSlideIndex === slideIndex - 1;
      const slideFromStartBehavior =
        !infinite && !canSlideForward && !isSlideBack && autoScrollInterval;

      const { offset } = getOffsetParams(carouselRef, slideFromStartBehavior ? 0 : newSlideIndex);
      performBrowserScroll(offset, 'smooth');
    },
    [autoScrollInterval, canSlideForward, infinite, slideIndex],
  );

  const slideBackHandler = useCallback(() => {
    onScrollLeft?.();
    slide(slideIndex - step);
  }, [onScrollLeft, slide, slideIndex, step]);

  const slideForwardHandler = useCallback(() => {
    onScrollRight?.();
    slide(slideIndex + step);
  }, [onScrollRight, slide, slideIndex, step]);

  const scrollEndHandler = useCallback(
    (e: Event): void => {
      const index = getIndexByElement(e.target as HTMLElement);
      const isEndOfScroll = isEndOfScrollReached(e.target as HTMLElement);

      if (isEndOfScroll && infinite) {
        performBrowserScroll(getOffsetParams(carouselRef, index - 1).offset, 'smooth');
        return;
      }

      setSlideIndex(index);
      setIsScrolling(false);
    },
    [infinite],
  );

  useScrollEnd(carouselRef, scrollEndHandler);

  const startAutoScroll = useCallback(() => {
    setAutoScrollInterval(autoScrollIntervalMs);
  }, [autoScrollIntervalMs]);

  const stopAutoScroll = useCallback(() => {
    setAutoScrollInterval(0);
  }, []);

  useEffect(() => {
    if (autoScrollIntervalMs) {
      startAutoScroll();
    } else {
      stopAutoScroll();
    }
  }, [autoScrollIntervalMs, startAutoScroll, stopAutoScroll]);

  useInterval(() => !isScrolling && slide(slideIndex + step), autoScrollInterval, []);

  const mouseLeaveHandler = useCallback(
    (event: MouseEvent) => {
      if (!carouselRef.current) return;

      const carouselParent = carouselRef.current.parentNode;
      const relatedTargetParent = (event.relatedTarget as Node)?.parentNode;

      if (carouselParent === relatedTargetParent) {
        return;
      }

      startAutoScroll();
    },
    [startAutoScroll],
  );

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = (): void => {
      if (!isScrolling) {
        setIsScrolling(true);
        carousel.removeEventListener('scroll', handleScroll);
      }
    };

    carousel.addEventListener('scroll', handleScroll);
    carousel.addEventListener('touchstart', stopAutoScroll, { passive: true });
    carousel.addEventListener('touchend', startAutoScroll);

    return () => {
      carousel.removeEventListener('scroll', handleScroll);
      carousel.removeEventListener('touchstart', stopAutoScroll);
      carousel.removeEventListener('touchend', startAutoScroll);
    };
  }, [isScrolling, startAutoScroll, stopAutoScroll]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || !pauseOnHover) return;

    carousel.addEventListener('mouseenter', stopAutoScroll);
    carousel.addEventListener('mouseleave', mouseLeaveHandler);

    return () => {
      carousel.removeEventListener('mouseenter', stopAutoScroll);
      carousel.removeEventListener('mouseleave', mouseLeaveHandler);
    };
  }, [stopAutoScroll, mouseLeaveHandler, pauseOnHover]);

  return {
    slideBack: slideBackHandler,
    slideForward: slideForwardHandler,
    canSlideBack,
    canSlideForward,
    carouselRef,
  };
};
